import { paths } from '@/routes/paths';

/**
 * Where a verification link lands when it was sent to unlock institutional
 * access: `/verify-email?verified=1&next=<path>`. The verify page sends the
 * browser on to `next` (a same-origin path only) once the link worked.
 */
export const buildVerifiedCallbackPath = (next: string): string =>
  `/verify-email?verified=1&next=${encodeURIComponent(next)}`;

/** Any absolute origin works: only "did `next` stay on it" matters. */
const PROBE_ORIGIN = 'https://ora.invalid';

/** `next` when it is a same-origin relative path, otherwise null. */
const readSafeNext = (next: string | null): string | null => {
  if (!next || !next.startsWith('/') || /^\/[/\\]/.test(next)) return null;

  try {
    const url = new URL(next, PROBE_ORIGIN);
    if (url.origin !== PROBE_ORIGIN) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
};

/**
 * The path a verified callback continues to: null when the URL is not one
 * (`verified=1` missing, or an `error` the backend added), else `next` when
 * safe, else home.
 */
export const readVerifiedRedirect = (search: string): string | null => {
  const params = new URLSearchParams(search);
  if (params.get('verified') !== '1' || params.has('error')) return null;

  return readSafeNext(params.get('next')) ?? paths.home;
};
